// ─────────────────────────────────────────────────────────────────────────────
// Site navigation: a fixed left side rail on desktop (content sits beside it,
// never under it) + an animated full-height menu panel shared with the mobile
// dock (BottomTabs). Deep navy / logo blue / white, from the HIBANE logo.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Briefcase, Film, Home, Info, LayoutGrid, Mail, PhoneCall, Calendar } from "lucide-react";
import Logo from "./Logo";
import { menu, useMenuOpen, PRIMARY, SOLUTIONS } from "./nav/menuStore";
import { ThemeSwitch } from "./nav/ThemeSwitch";

const ease = [0.76, 0, 0.24, 1] as const;
const easeOut = [0.16, 1, 0.3, 1] as const;

const RAIL = [
  { label: "Home", to: "/home", icon: Home },
  { label: "About", to: "/about", icon: Info },
  { label: "Services", to: "/services", icon: Briefcase },
  { label: "Solutions", to: "/suite", icon: LayoutGrid, match: /^\/(suite|hrdesk|financedesk)/ },
  { label: "Media", to: "/media", icon: Film },
  { label: "Voice Agent", to: "/voiceagent", icon: PhoneCall },
  { label: "Contact", to: "/contact", icon: Mail },
];

/** Animated two-bar burger that morphs into an X. */
export const Burger = ({ open, className = "" }: { open: boolean; className?: string }) => (
  <span className={`relative block h-3 w-6 ${className}`} aria-hidden>
    <motion.span
      className="absolute left-0 top-0 block h-[1.5px] w-full bg-current"
      animate={open ? { rotate: 45, y: 5.25 } : { rotate: 0, y: 0 }}
      transition={{ duration: 0.45, ease }}
    />
    <motion.span
      className="absolute bottom-0 left-0 block h-[1.5px] bg-current"
      animate={open ? { rotate: -45, y: -5.25, width: "100%" } : { rotate: 0, y: 0, width: "62%" }}
      transition={{ duration: 0.45, ease }}
    />
  </span>
);

function SideRail() {
  const { pathname } = useLocation();
  const open = useMenuOpen();
  const [hover, setHover] = useState<string | null>(null);
  return (
    <aside
      data-fx-inset-left
      className="fixed inset-y-0 left-0 z-[60] hidden w-[76px] flex-col items-center border-r border-cx-accent/15 bg-cx-bg/95 py-5 backdrop-blur md:flex"
    >
      <Link to="/home" aria-label="HIBANE AI home" className="mb-6 block w-12">
        <Logo size="sm" variant="auto" />
      </Link>

      <button
        type="button"
        onClick={menu.toggle}
        aria-expanded={open}
        aria-controls="site-menu"
        aria-label={open ? "Close menu" : "Open menu"}
        className="group mb-6 flex h-11 w-11 items-center justify-center rounded-full border border-cx-accent/40 text-cx-fg transition-colors hover:border-cx-accent hover:text-cx-link"
      >
        <Burger open={open} />
      </button>

      <nav aria-label="Primary" className="flex flex-1 flex-col items-center gap-1">
        {RAIL.map(({ label, to, icon: Icon, match }) => {
          const active = match ? match.test(pathname) : pathname === to;
          return (
            <Link
              key={to}
              to={to}
              aria-label={label}
              onMouseEnter={() => setHover(to)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(to)}
              onBlur={() => setHover(null)}
              className={`relative flex h-11 w-11 items-center justify-center rounded-xl transition-colors ${
                active ? "text-cx-link" : "text-cx-fg/60 hover:text-cx-fg"
              }`}
            >
              {active && (
                <motion.span
                  layoutId="rail-active"
                  className="absolute -left-[16px] top-2 bottom-2 w-[2px] rounded-full bg-cx-accent"
                  transition={{ type: "spring", stiffness: 420, damping: 34 }}
                />
              )}
              <Icon className="h-[18px] w-[18px]" strokeWidth={1.6} />
              <AnimatePresence>
                {hover === to && (
                  <motion.span
                    initial={{ opacity: 0, x: -8, clipPath: "inset(0 100% 0 0)" }}
                    animate={{ opacity: 1, x: 0, clipPath: "inset(0 0% 0 0)" }}
                    exit={{ opacity: 0, x: -6, clipPath: "inset(0 100% 0 0)" }}
                    transition={{ duration: 0.35, ease: easeOut }}
                    className="pointer-events-none absolute left-[58px] whitespace-nowrap rounded-md border border-cx-accent/30 bg-cx-bg px-3 py-1.5 font-hud text-[11px] uppercase tracking-[0.18em] text-cx-fg"
                  >
                    {label}
                  </motion.span>
                )}
              </AnimatePresence>
            </Link>
          );
        })}
      </nav>

      <ThemeSwitch variant="icon" className="mb-3" />
      <Link
        to="/consultation-intro"
        aria-label="Book a consultation"
        className="flex h-11 w-11 items-center justify-center rounded-full bg-[#2A629E] text-white transition-transform hover:scale-105"
      >
        <Calendar className="h-[18px] w-[18px]" strokeWidth={1.8} />
      </Link>
    </aside>
  );
}

function MenuPanel() {
  const open = useMenuOpen();
  const { pathname } = useLocation();

  useEffect(() => menu.close(), [pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && menu.close();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="scrim"
            className="fixed inset-0 z-[55] bg-cx-scrim/70 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            onClick={menu.close}
          />
          <motion.div
            key="panel"
            id="site-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Site menu"
            data-state="open"
            className="fx-grain fixed inset-y-0 left-0 z-[58] flex w-full flex-col overflow-y-auto bg-cx-bg text-cx-fg md:left-[76px] md:w-[min(960px,calc(100vw-76px))] md:border-r md:border-cx-accent/20"
            initial={{ clipPath: "inset(0 100% 0 0)" }}
            animate={{ clipPath: "inset(0 0% 0 0)" }}
            exit={{ clipPath: "inset(0 100% 0 0)" }}
            transition={{ duration: 0.7, ease }}
          >
            {/* mobile header row */}
            <div className="flex items-center justify-between px-6 pt-6 md:hidden">
              <Link to="/home" className="block w-24">
                <Logo size="sm" variant="auto" />
              </Link>
              <button type="button" onClick={menu.close} aria-label="Close menu" className="p-2 text-cx-fg">
                <Burger open />
              </button>
            </div>

            <div className="grid flex-1 gap-12 px-6 pb-28 pt-10 md:grid-cols-12 md:px-14 md:pb-14 md:pt-16">
              <nav aria-label="Main" className="md:col-span-7">
                <p className="mb-6 font-hud text-[11px] uppercase tracking-[0.24em] text-cx-link">Index</p>
                <ul className="space-y-1">
                  {PRIMARY.map((l, i) => {
                    const active = pathname === l.to;
                    return (
                      <li key={l.to} className="overflow-hidden">
                        <motion.div
                          initial={{ y: "110%" }}
                          animate={{ y: "0%" }}
                          exit={{ y: "110%" }}
                          transition={{ duration: 0.8, ease: easeOut, delay: 0.18 + i * 0.05 }}
                        >
                          <Link
                            to={l.to}
                            className={`group flex items-baseline gap-4 py-1 font-cine text-[clamp(2.1rem,5.2vw,3.6rem)] leading-[1.02] tracking-[-0.035em] transition-colors ${
                              active ? "text-cx-link" : "hover:text-cx-link"
                            }`}
                          >
                            <span className="transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-3">
                              {l.label}
                            </span>
                          </Link>
                        </motion.div>
                      </li>
                    );
                  })}
                </ul>
              </nav>

              <motion.aside
                className="space-y-10 md:col-span-5 md:border-l md:border-cx-fg/10 md:pl-10"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.7, ease: easeOut, delay: 0.4 }}
              >
                <div>
                  <p className="mb-3 font-hud text-[11px] uppercase tracking-[0.24em] text-cx-link">Appearance</p>
                  <ThemeSwitch variant="pill" />
                </div>

                <div>
                  <p className="mb-4 font-hud text-[11px] uppercase tracking-[0.24em] text-cx-link">Solutions</p>
                  <ul className="divide-y divide-cx-fg/10 border-y border-cx-fg/10">
                    {SOLUTIONS.map((s) => (
                      <li key={s.to}>
                        <Link to={s.to} className="group flex items-center justify-between gap-4 py-3">
                          <span>
                            <span className="block text-[15px] font-medium">{s.label}</span>
                            <span className="block text-[13px] text-cx-muted">{s.note}</span>
                          </span>
                          <ArrowRight className="h-4 w-4 shrink-0 text-cx-muted transition-all group-hover:translate-x-1 group-hover:text-cx-link" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex flex-col gap-3">
                  <Link
                    to="/consultation-intro"
                    className="inline-flex items-center justify-between rounded-full bg-[#2A629E] px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#3A78BD] transition-colors transition-transform hover:scale-[1.02]"
                  >
                    Book a consultation <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link
                    to="/hub-registration"
                    className="inline-flex items-center justify-between rounded-full border border-cx-fg/25 px-6 py-3.5 text-sm font-semibold transition-colors hover:border-cx-accent hover:text-cx-link"
                  >
                    Join the HIBANE AI Hub <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>

                <Link to="/suite" className="block rounded-xl border border-cx-accent/25 p-4 transition-colors hover:border-cx-accent/60">
                  <span className="font-hud text-[10px] uppercase tracking-[0.22em] text-cx-link">New</span>
                  <span className="mt-1 block text-sm">HR Desk + HIBANE Books are now live.</span>
                </Link>

                <div className="font-hud text-[11px] uppercase leading-relaxed tracking-[0.16em] text-cx-muted">
                  <a href="mailto:info@hibane.ai" className="block hover:text-cx-fg">info@hibane.ai</a>
                  <a href="tel:+233596992641" className="block hover:text-cx-fg">+233 596 992 641</a>
                  <span className="mt-3 block">Accra · Lagos · Kigali</span>
                </div>
              </motion.aside>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

const Navigation = () => (
  <>
    <SideRail />
    <MenuPanel />
  </>
);

export default Navigation;
